import type { ReactNode } from "react";
import { Phone } from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useIsMobile } from "@/hooks/use-mobile";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import type { DealCard, TaskCard } from "../types";
import {
  fullName,
  toneClass,
  useCardStatusText,
  useCardSubtitle,
} from "./hooks";
import { TaskRow } from "./TaskRow";
import { endOfTodayIso, taskTiming } from "./todayLogic";

type Filter = "all" | "new" | "overdue";

/**
 * Manager's home on the phone (docs/design/Main.dc.html): new leads nobody
 * called yet, then my tasks due today or overdue.
 */
export const TodayPage = () => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { identity } = useGetIdentity();
  const [filter, setFilter] = useState<Filter>("all");
  const now = new Date();
  const initials = (identity?.fullName ?? "")
    .split(" ")
    .map((part: string) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  // RLS already narrows a manager to their own leads
  const { data: toCall = [], total: toCallTotal = 0 } = useGetList<DealCard>(
    "deal_cards",
    {
      filter: {
        stage: "new",
        "last_call_at@is": null,
        "first_action_at@is": null,
        "archived_at@is": null,
      },
      pagination: { page: 1, perPage: 500 },
      sort: { field: "lead_created_at", order: "ASC" },
    },
  );
  const { data: tasks = [] } = useGetList<TaskCard>(
    "task_cards",
    {
      filter: {
        "done_date@is": null,
        sales_id: identity?.id,
        "due_date@lt": endOfTodayIso(now),
      },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "due_date", order: "ASC" },
    },
    { enabled: identity?.id != null },
  );
  const overdue = tasks.filter(
    (t) => taskTiming(t.due_date, now) === "overdue",
  );

  const date = now.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const chips: {
    value: Filter;
    label: string;
    count: number;
    danger?: boolean;
  }[] = [
    {
      value: "all",
      label: translate("crm.today.all"),
      count: toCallTotal + tasks.length,
    },
    { value: "new", label: translate("crm.today.new"), count: toCallTotal },
    {
      value: "overdue",
      label: translate("crm.today.overdue"),
      count: overdue.length,
      danger: true,
    },
  ];
  const shownTasks =
    filter === "overdue" ? overdue : filter === "new" ? [] : tasks;
  const showCalls = filter !== "overdue";

  // On a computer Today lives inside the normal layout, without the phone header
  const Content = isMobile ? MobileContent : DesktopContent;

  return (
    <>
      {isMobile && (
        <MobileHeader>
          <div className="flex items-center gap-3">
            {/* Profile, settings and log out live behind the avatar (docs/design/Main) */}
            <Link
              to="/settings"
              aria-label={translate("crm.settings.title")}
              className="flex size-9 items-center justify-center rounded-full bg-accent text-sm font-bold text-primary no-underline"
            >
              {initials}
            </Link>
            <span className="inline-flex items-center text-xl font-extrabold tracking-tight text-primary">
              Oselya
              <i
                aria-hidden
                className="ml-[0.06em] inline-block size-[0.3em] rounded-full bg-honey"
              />
            </span>
          </div>
        </MobileHeader>
      )}
      <Content>
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">
              {translate("crm.today.title")}
            </h1>
            <div className="text-sm text-muted-foreground first-letter:uppercase">
              {date}
            </div>
          </div>
        </div>

        <div className="-mx-4 mb-5 flex gap-2 overflow-x-auto px-4">
          {chips.map((chip) => (
            <button
              key={chip.value}
              type="button"
              aria-pressed={filter === chip.value}
              onClick={() => setFilter(chip.value)}
              className={cn(
                "shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold",
                filter === chip.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : chip.danger && chip.count > 0
                    ? "border-transparent bg-destructive/10 text-destructive"
                    : "bg-card",
              )}
            >
              {chip.label} · {chip.count}
            </button>
          ))}
        </div>

        {showCalls && toCall.length > 0 ? (
          <section className="mb-6 flex flex-col gap-3">
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {translate("crm.today.call_now")}
            </h2>
            {toCall.map((card) => (
              <CallNowCard key={card.id} card={card} now={now} />
            ))}
          </section>
        ) : null}

        {shownTasks.length > 0 ? (
          <section className="flex flex-col gap-3">
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {translate("crm.today.tasks_today")}
            </h2>
            {shownTasks.map((task) => (
              <TaskRow key={task.id} task={task} />
            ))}
          </section>
        ) : null}

        {(showCalls ? toCall.length : 0) + shownTasks.length === 0 ? (
          <p className="mt-8 text-center text-muted-foreground">
            {translate("crm.today.nothing")}
          </p>
        ) : null}
      </Content>
    </>
  );
};

const DesktopContent = ({ children }: { children: ReactNode }) => (
  <div className="max-w-5xl">{children}</div>
);

TodayPage.path = "/today";

const CallNowCard = ({ card, now }: { card: DealCard; now: Date }) => {
  const translate = useTranslate();
  const statusText = useCardStatusText();
  const subtitle = useCardSubtitle();
  const status = statusText(card, now);
  return (
    <div className="flex items-center gap-3 rounded-2xl border bg-card p-4">
      <Link
        to={`/funnel/${card.id}`}
        className="min-w-0 flex-1 text-foreground no-underline"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-bold">{fullName(card)}</span>
          <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-semibold text-primary">
            {translate("crm.today.new_badge")}
          </span>
        </div>
        <div className="truncate text-sm text-muted-foreground">
          {subtitle(card)}
        </div>
        {status ? (
          <div className={cn("text-sm font-bold", toneClass[status.tone])}>
            {status.text}
          </div>
        ) : null}
      </Link>
      {card.phone ? (
        <a
          href={`tel:${card.phone.replace(/[^\d+]/g, "")}`}
          aria-label={translate("crm.today.call", { name: fullName(card) })}
          className={cn(
            buttonVariants({ size: "icon" }),
            "size-12 shrink-0 rounded-full",
          )}
        >
          <Phone className="size-5" />
        </a>
      ) : null}
    </div>
  );
};
